import React, { useEffect, useRef, useMemo } from "react";
import { Card } from "antd";
import Chart from "chart.js/auto";

const MonthlyChart = ({ data }) => {
  const chartRef = useRef(null);
  const [chartType, setChartType] = React.useState("sale");
  // Process the data from the backend or use default sample data
  const chartData = useMemo(() => {
    // Default months and data
    const defaultMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const defaultSalesData = Array(12).fill(0).map(() => Math.floor(Math.random() * 40000) + 10000);
    const defaultProfitData = Array(12).fill(0).map(() => Math.floor(Math.random() * 10000) + 5000);
    
    // Default result structure
    const defaultResult = {
      labels: defaultMonths,
      sales: defaultSalesData,
      profit: defaultProfitData
    };
    
    
    // If no data is provided, return the default data
    if (!data) {
      return defaultResult;
    }
    
    try {
      // CASE 1: Data is an array of objects with month, sales, profit (from backend)
      if (Array.isArray(data)) {
        
        // Check if we have the expected format (array of objects with month in 'YYYY-MM' format)
        if (data.length > 0 && data[0].month && typeof data[0].month === 'string') {
          
          
          // Sort data by month to ensure chronological order
          const sortedData = [...data].sort((a, b) => a.month.localeCompare(b.month));
          
          // Extract labels, sales and profit directly from the data
          const labels = sortedData.map(item => item.month); // Use YYYY-MM format directly
          const salesData = sortedData.map(item => Number(item.sales || 0));
          const profitData = sortedData.map(item => Number(item.profit || 0));
          
          return { labels, sales: salesData, profit: profitData };
        }
      }
      
      // CASE 2: Data is an object with sales and profit properties (legacy format)
      if (data.sales !== undefined || data.profit !== undefined) {
        
        
        const salesData = Array.isArray(data.sales) ? data.sales : defaultSalesData;
        const profitData = Array.isArray(data.profit) ? data.profit : defaultProfitData;
        const labels = data.labels || defaultMonths;
        
        return { labels, sales: salesData, profit: profitData };
      }
      
      // If we couldn't process the data in any known format, use defaults
      return defaultResult;
    } catch (error) {
      console.error('Error processing monthly chart data:', error);
      return defaultResult;
    }
  }, [data]);
  
  useEffect(() => {
    if (chartRef.current) {
      const existingChart = Chart.getChart(chartRef.current);
      if (existingChart) existingChart.destroy();

      const ctx = chartRef.current.getContext("2d");
      
      // Use the processed data
      const salesData = chartData.sales;
      const profitData = chartData.profit;
      
      new Chart(ctx, {
        type: "bar",
        data: {
          labels: chartData.labels,
          datasets: [
            {
              label: "Sales",
              data: salesData,
              backgroundColor: "#6f42c1",
              borderColor: "#6f42c1",
              borderWidth: 1,
              hidden: chartType !== "sale" && chartType !== "both",
            },
            {
              label: "Profit",
              data: profitData,
              backgroundColor: "#007bff",
              borderColor: "#007bff",
              borderWidth: 1,
              hidden: chartType !== "profit" && chartType !== "both",
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return '$' + value.toLocaleString();
                }
              }
            }
          },
          plugins: {
            datalabels: {
              display: false,
            },
            legend: {
              display: true,
              position: "top"
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return `${context.dataset.label}: $${context.raw.toLocaleString()}`;
                }
              }
            }
          }
        }
      });
    }
  }, [chartType, chartData]);



  return (
    <Card 
      title="MONTHLY" 
      className="dashboard-card"
    >
      <div style={{ height: "300px" }}>
        <canvas ref={chartRef}></canvas>
      </div>
    </Card>
  );
};

export default MonthlyChart;
